import { memo, useMemo } from "react";
import { Typography, Input, Row, Col, Select, Image, Dropdown, Button, Tooltip, Spin } from "antd";
import { DownloadOutlined, FilePdfOutlined, FileExcelOutlined } from "@ant-design/icons";
import PrimaryButton from "../CustomButton/PrimaryButton";
import ColumnSettingsDropdown from "./ColumnSettingsDropdown";
import ArrowDownIcon from "../../assets/icons/arrow-down-icon.svg";
import "./CustomTable.scss";

const { Text } = Typography;

const TableHeader = ({
    title,
    searchText,
    setSearchText,
    btnName,
    handleBtnOnClick,
    btnIcon = null,
    isDropdown,
    dropdownData = [],
    selectedStatus,
    handleStatusChange,
    statusDisplayNames = {},
    onDownloadPdf,
    onDownloadExcel,
    isDownloadRequired,
    downloadLoading = false,
    statusLoading = false,
    columnControl = null,
    extraControls = null,
    leftContent = null,
}) => {
    const desiredStatusIds = useMemo(
        () => Object.keys(statusDisplayNames),
        [statusDisplayNames],
    );

    const filteredStatusList = useMemo(
        () => dropdownData.filter((status) => desiredStatusIds.includes(status.statusId)),
        [dropdownData, desiredStatusIds],
    );

    const downloadItems = useMemo(
        () => [
            {
                key: "pdf",
                label: "Download as PDF",
                onClick: onDownloadPdf,
                icon: <FilePdfOutlined />,
            },
            {
                key: "excel",
                label: "Download as Excel",
                onClick: onDownloadExcel,
                icon: <FileExcelOutlined />,
            },
        ],
        [onDownloadPdf, onDownloadExcel],
    );

    const statusOptions = useMemo(
        () => filteredStatusList.map((status) => ({
            value: status.statusId,
            statusName: statusDisplayNames[status.statusId] || status.statusId,
            label: status.count !== undefined
                ? `${statusDisplayNames[status.statusId]} (${status.count})`
                : statusDisplayNames[status.statusId],
        })),
        [filteredStatusList, statusDisplayNames],
    );

    return (
        <div className="table-header">
            <Row align="middle" justify="space-between" gutter={[12, 10]}>
                <Col flex="none" className="table-header-title-col">
                    {leftContent || (title ? <Text strong className="table-title">{title}</Text> : null)}
                </Col>

                <Col flex="auto" className="table-header-toolbar-col">
                    <div className="search-select-btn-container table-toolbar-controls">
                        {isDownloadRequired && (
                            <Dropdown
                                menu={{ items: downloadItems }}
                                trigger={["click"]}
                                disabled={downloadLoading}
                            >
                                <Tooltip title={downloadLoading ? "Downloading..." : "Download"}>
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        className="download-btn"
                                        icon={
                                            downloadLoading
                                                ? <Spin size="small" />
                                                : <DownloadOutlined />
                                        }
                                        disabled={downloadLoading}
                                    />
                                </Tooltip>
                            </Dropdown>
                        )}

                        {columnControl && (
                            <ColumnSettingsDropdown
                                availableOptions={columnControl.availableOptions}
                                visibleOrderedKeys={columnControl.visibleOrderedKeys}
                                onToggleColumn={columnControl.onToggleColumn}
                                onToggleAll={columnControl.onToggleAll}
                                onReorder={columnControl.onReorder}
                                onReset={columnControl.onReset}
                                loading={columnControl.loading}
                                saving={columnControl.saving}
                                isCustomized={columnControl.isCustomized}
                            />
                        )}

                        {extraControls}

                        <Input
                            placeholder="Search"
                            allowClear
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="search-input"
                        />

                        {isDropdown && (
                            <Select
                                placeholder={statusLoading ? "Loading status..." : "Select Status"}
                                className="status-dropdown"
                                popupClassName="status-dropdown-popup"
                                placement="bottomRight"
                                loading={statusLoading}
                                disabled={statusLoading}
                                mode={Array.isArray(selectedStatus) ? "multiple" : undefined}
                                optionLabelProp="statusName"
                                maxTagCount={3}
                                maxTagPlaceholder={(omittedValues) => (
                                    <Tooltip
                                        title={omittedValues
                                            .map((val) => statusDisplayNames[val?.value] || val?.label || val?.value || val)
                                            .join(", ")}
                                    >
                                        <span className="status-dropdown-rest-count">+{omittedValues.length}</span>
                                    </Tooltip>
                                )}
                                suffixIcon={
                                    statusLoading
                                        ? <Spin size="small" />
                                        : (
                                            <Image
                                                src={ArrowDownIcon}
                                                preview={false}
                                            />
                                        )
                                }
                                value={selectedStatus}
                                onChange={handleStatusChange}
                                options={statusOptions}
                            />
                        )}

                        {btnName && (
                            <PrimaryButton
                                className="table-header-btn"
                                onClick={handleBtnOnClick}
                                icon={btnIcon}
                                style={{
                                    height: 38,
                                    padding: "0 16px",
                                    lineHeight: "36px",
                                    width: "auto",
                                    minWidth: "fit-content",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {btnName}
                            </PrimaryButton>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default memo(TableHeader);
